import { useCallback, useEffect, useState } from "react";
import { api, mesajEroare } from "./api.js";
import { FRECVENTE, REZULTATE, eticheta } from "./enums.js";
import { data } from "./text.js";
import { tr } from "./i18n.js";

const PLANIFICARE = ["ADMIN_TENANT", "MANAGER_TEHNIC", "DISPECER", "COORDONATOR"];
const TEREN = [...PLANIFICARE, "TEHNICIAN"];


/** Cât a mai rămas, citit ca un om, nu ca un număr de zile cu semn. */
function panaLa(zile) {
  if (zile < 0) return { text: tr("depășit cu {0} {1}", -zile, -zile === 1 ? "zi" : "zile"), clasa: "scadent-depasit" };
  if (zile === 0) return { text: "azi", clasa: "scadent-azi" };
  if (zile <= 14) return { text: tr("în {0} {1}", zile, zile === 1 ? "zi" : "zile"), clasa: "scadent-aproape" };
  return { text: tr("în {0} zile", zile), clasa: "" };
}

export default function Mentenanta({ eu }) {
  const [sub, setSub] = useState("scadentar");
  const poatePlanifica = PLANIFICARE.includes(eu.rol);

  return (
    <div className="continut">
      <h1>{tr("Mentenanță preventivă")}</h1>
      <p className="subtitlu">
        {tr("Șabloanele de verificare devin checklist-uri de teren, iar aplicația își generează singură vizitele înainte de scadență. Un șablon deja rulat nu se mai editează — se face o versiune nouă, iar execuțiile vechi rămân cum au fost.")}
      </p>

      <div className="taburi-sisteme">
        {[
          ["scadentar", "Scadențar"],
          ["planuri", "Planuri"],
          ["sabloane", "Șabloane"],
        ].map(([cod, titlu]) => (
          <button key={cod} className={"tab-sistem" + (sub === cod ? " activ" : "")}
            onClick={() => setSub(cod)}>
            {tr(titlu)}
          </button>
        ))}
      </div>

      {sub === "scadentar" ? <Scadentar eu={eu} poatePlanifica={poatePlanifica} />
        : sub === "planuri" ? <Planuri eu={eu} poatePlanifica={poatePlanifica} />
          : <Sabloane eu={eu} poatePlanifica={poatePlanifica} />}
    </div>
  );
}

// ── Scadențar ──────────────────────────────────────────────────────────────

function Scadentar({ poatePlanifica }) {
  const [zile, setZile] = useState(60);
  const [lista, setLista] = useState([]);
  const [incarca, setIncarca] = useState(true);
  const [eroare, setEroare] = useState("");
  const [raport, setRaport] = useState(null);

  const reincarca = useCallback(async () => {
    setIncarca(true);
    try { setLista(await api.scadentarMentenanta(zile)); }
    catch (e) { setEroare(mesajEroare(e)); }
    finally { setIncarca(false); }
  }, [zile]);

  useEffect(() => { reincarca(); }, [reincarca]);

  return (
    <>
      {eroare && <div className="eroare">{eroare}</div>}

      <div className="panou">
        <div className="panou-corp">
          <div className="linie-actiune" style={{ alignItems: "center" }}>
            <label className="camp camp-larg">{tr("Orizont")}
              <select value={zile} onChange={(e) => setZile(Number(e.target.value))}>
                <option value={30}>{tr("următoarele 30 de zile")}</option>
                <option value={60}>{tr("următoarele 60 de zile")}</option>
                <option value={180}>{tr("următoarele 6 luni")}</option>
                <option value={365}>{tr("următorul an")}</option>
              </select>
            </label>
            {poatePlanifica && (
              <button className="buton buton-actiune"
                onClick={async () => {
                  setEroare("");
                  try {
                    const r = await api.genereazaVizite();
                    setRaport(r);
                    await reincarca();
                  } catch (e) { setEroare(mesajEroare(e)); }
                }}>
                {tr("generează vizitele scadente")}
              </button>
            )}
          </div>
          <p className="nota-cautare">
            {tr("Vizitele se generează oricum singure, la fiecare oră. Butonul e pentru când nu vrei să aștepți.")}
          </p>
        </div>
      </div>

      {raport && (
        <div className="panou panou-atentie">
          <div className="panou-corp">
            <strong>
              {raport.vizite === 0
                ? tr("Nicio vizită nouă — nimic nu a intrat încă în fereastra de preaviz.")
                : `${raport.vizite} ${raport.vizite === 1 ? "vizită generată" : "vizite generate"} din ${raport.planuri} planuri.`}
            </strong>
            {(raport.sarite ?? []).length > 0 && (
              <ul className="lista-erori">
                {raport.sarite.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            )}
          </div>
        </div>
      )}

      {incarca ? (
        <div className="gol" style={{ padding: "2rem 0" }}>{tr("Se încarcă…")}</div>
      ) : lista.length === 0 ? (
        <div className="panou"><div className="panou-corp gol">
          {tr("Nimic scadent în intervalul ales.")}
        </div></div>
      ) : (
        <div className="panou">
          <table className="tabel">
            <thead>
              <tr>
                <th>{tr("Client")}</th><th>{tr("Locație / sistem")}</th><th>{tr("Șablon")}</th>
                <th>{tr("Frecvență")}</th><th>{tr("Scadență")}</th>
              </tr>
            </thead>
            <tbody>
              {lista.map((p) => {
                const s = panaLa(p.zilePanaLaScadenta);
                return (
                  <tr key={p.id}>
                    <td>{tr(p.client?.denumire)}</td>
                    <td>
                      {tr(p.sistemInstalat?.denumire)}
                      <span className="fara-pret">{tr(p.sistemInstalat?.locatie?.denumire)}</span>
                    </td>
                    <td>{tr(p.sablon?.denumire)} <span className="cod-mic">{tr("v")}{p.sablon?.versiune}</span></td>
                    <td>{p.frecventaEticheta}</td>
                    <td>
                      <span className={"scadenta " + s.clasa}>{s.text}</span>
                      <span className="fara-pret">{data(p.urmatoareaScadenta)}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ── Planuri ────────────────────────────────────────────────────────────────

function Planuri({ poatePlanifica }) {
  const [clienti, setClienti] = useState([]);
  const [clientId, setClientId] = useState("");
  const [lista, setLista] = useState([]);
  const [inactive, setInactive] = useState(false);
  const [form, setForm] = useState(null);
  const [incarca, setIncarca] = useState(true);
  const [eroare, setEroare] = useState("");

  const reincarca = useCallback(async () => {
    setIncarca(true);
    try { setLista(await api.planuriMentenanta({ clientId: clientId || undefined, inactive })); }
    catch (e) { setEroare(mesajEroare(e)); }
    finally { setIncarca(false); }
  }, [clientId, inactive]);

  useEffect(() => { reincarca(); }, [reincarca]);
  useEffect(() => { api.clienti().then(setClienti).catch(() => {}); }, []);

  return (
    <>
      {eroare && <div className="eroare">{eroare}</div>}
      <div className="panou">
        <div className="panou-corp">
          <div className="linie-actiune" style={{ alignItems: "center" }}>
            <label className="camp">{tr("Client")}
              <select value={clientId} onChange={(e) => setClientId(e.target.value)}>
                <option value="">{tr("toți clienții")}</option>
                {clienti.map((c) => <option key={c.id} value={c.id}>{tr(c.denumire)}</option>)}
              </select>
            </label>
            <label className="comutator-dezactivate">
              <input type="checkbox" checked={inactive}
                onChange={(e) => setInactive(e.target.checked)} />
              {tr("arată și planurile oprite")}
            </label>
            {poatePlanifica && (
              <button className="buton buton-actiune" onClick={() => setForm({})}>{tr("plan nou")}</button>
            )}
          </div>
        </div>
      </div>

      {incarca ? (
        <div className="gol" style={{ padding: "2rem 0" }}>{tr("Se încarcă…")}</div>
      ) : lista.length === 0 ? (
        <div className="panou"><div className="panou-corp gol">
          {tr("Niciun plan de mentenanță. Fără el, scadențele nu se calculează singure.")}
        </div></div>
      ) : (
        <div className="panou">
          <table className="tabel">
            <thead>
              <tr>
                <th>{tr("Client")}</th><th>{tr("Sistem")}</th><th>{tr("Șablon")}</th><th>{tr("Frecvență")}</th>
                <th>{tr("Scadență")}</th><th>{tr("Preaviz")}</th><th />
              </tr>
            </thead>
            <tbody>
              {lista.map((p) => {
                const s = panaLa(p.zilePanaLaScadenta);
                return (
                  <tr key={p.id} className={p.activ ? "" : "tip-dezactivat"}>
                    <td>{tr(p.client?.denumire)}</td>
                    <td>
                      {tr(p.sistemInstalat?.denumire)}
                      <span className="fara-pret">{tr(p.sistemInstalat?.locatie?.denumire)}</span>
                    </td>
                    <td>{tr(p.sablon?.denumire)} <span className="cod-mic">{tr("v")}{p.sablon?.versiune}</span></td>
                    <td>{p.frecventaEticheta}</td>
                    <td>
                      <span className={"scadenta " + s.clasa}>{s.text}</span>
                      <span className="fara-pret">{data(p.urmatoareaScadenta)}</span>
                    </td>
                    <td>{p.zilePreaviz} {tr("zile")}</td>
                    <td className="celula-actiuni">
                      {poatePlanifica && (
                        <button className="buton buton-mic buton-discret"
                          onClick={async () => {
                            try {
                              await api.actualizeazaPlan(p.id, { activ: !p.activ });
                              await reincarca();
                            } catch (e) { setEroare(mesajEroare(e)); }
                          }}>
                          {p.activ ? tr("oprește") : tr("repornește")}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {form && (
        <PlanNou
          clienti={clienti}
          laInchidere={() => setForm(null)}
          laSalvare={async () => { setForm(null); await reincarca(); }}
        />
      )}
    </>
  );
}

function PlanNou({ clienti, laInchidere, laSalvare }) {
  const [clientId, setClientId] = useState("");
  const [arbore, setArbore] = useState([]);
  const [sistemId, setSistemId] = useState("");
  const [sabloane, setSabloane] = useState([]);
  const [sablonId, setSablonId] = useState("");
  const [frecventa, setFrecventa] = useState("SA");
  const [dataStart, setDataStart] = useState(new Date().toISOString().slice(0, 10));
  const [zilePreaviz, setZilePreaviz] = useState(14);
  const [lucreaza, setLucreaza] = useState(false);
  const [eroare, setEroare] = useState("");

  useEffect(() => { api.sabloaneMentenanta().then(setSabloane).catch(() => {}); }, []);
  useEffect(() => {
    if (!clientId) { setArbore([]); return; }
    setSistemId("");
    api.parc(clientId).then(setArbore).catch((e) => setEroare(mesajEroare(e)));
  }, [clientId]);

  const sisteme = arbore.flatMap((l) =>
    (l.sisteme ?? []).map((s) => ({ ...s, locatie: l.denumire })));
  const sistemAles = sisteme.find((s) => s.id === sistemId);
  // Un checklist de video pe un sistem de control acces n-ar avea ce verifica.
  const potrivite = sistemAles
    ? sabloane.filter((s) => s.tipSistem?.id === sistemAles.tipSistem?.id)
    : sabloane;

  const faraSisteme = !!clientId && sisteme.length === 0;

  return (
    <div className="modal-fundal" onClick={laInchidere}>
      <div className="modal modal-lat" onClick={(e) => e.stopPropagation()}>
        <h3>{tr("Plan de mentenanță nou")}</h3>
        {eroare && <div className="eroare">{eroare}</div>}

        <div className="formular-linie">
          <label className="camp">{tr("Client")}
            <select value={clientId} onChange={(e) => setClientId(e.target.value)}>
              <option value="">{tr("— alege —")}</option>
              {clienti.map((c) => <option key={c.id} value={c.id}>{tr(c.denumire)}</option>)}
            </select>
          </label>
          <label className="camp">{tr("Sistem instalat")}
            <select value={sistemId} onChange={(e) => { setSistemId(e.target.value); setSablonId(""); }}
              disabled={faraSisteme}>
              <option value="">{faraSisteme ? tr("— clientul n-are niciun sistem —") : tr("— alege —")}</option>
              {sisteme.map((s) => (
                <option key={s.id} value={s.id}>{tr(s.denumire)} — {s.locatie}</option>
              ))}
            </select>
          </label>
        </div>

        {faraSisteme && (
          <div className="panou panou-atentie"><div className="panou-corp">
            <strong>{tr("Clientul n-are niciun sistem în parcul instalat.")}</strong>{" "}
            {tr("Mentenanța se planifică pe un sistem, deci întâi se adaugă parcul la „Parc instalat\".")}
          </div></div>
        )}

        <div className="formular-linie">
          <label className="camp">{tr("Șablon")}
            <select value={sablonId} onChange={(e) => setSablonId(e.target.value)}
              disabled={!sistemId}>
              <option value="">
                {!sistemId ? tr("— alege întâi sistemul —")
                  : potrivite.length === 0 ? tr("— niciun șablon pentru acest tip de sistem —")
                    : tr("— alege —")}
              </option>
              {potrivite.map((s) => (
                <option key={s.id} value={s.id}>
                  {tr(s.denumire)} {tr("(v")}{s.versiune}, {s._count?.linii} {tr("rânduri)")}
                </option>
              ))}
            </select>
          </label>
          <label className="camp">{tr("Frecvență")}
            <select value={frecventa} onChange={(e) => setFrecventa(e.target.value)}>
              {Object.entries(FRECVENTE).map(([v, e]) => <option key={v} value={v}>{tr(e)}</option>)}
            </select>
          </label>
        </div>

        <div className="formular-linie">
          <label className="camp">{tr("Începe de la")}
            <input type="date" value={dataStart} onChange={(e) => setDataStart(e.target.value)} />
          </label>
          <label className="camp">{tr("Preaviz (zile)")}
            <input type="number" min={0} max={180} value={zilePreaviz}
              onChange={(e) => setZilePreaviz(Number(e.target.value))} />
          </label>
        </div>

        <p className="nota-cautare">
          {tr("Vizita se naște cu atâtea zile înainte de scadență. O dată de început din trecut nu produce restanțe în serie: scadența sare înainte până trece de azi.")}
        </p>

        <div className="modal-actiuni">
          <button className="buton buton-sters" onClick={laInchidere}>{tr("renunță")}</button>
          <button className="buton buton-actiune"
            disabled={lucreaza || !sistemId || !sablonId}
            onClick={async () => {
              setLucreaza(true); setEroare("");
              try {
                await api.creeazaPlan({
                  sistemInstalatId: sistemId, sablonId, frecventa,
                  dataStart: new Date(dataStart).toISOString(), zilePreaviz,
                });
                await laSalvare();
              } catch (e) { setEroare(mesajEroare(e)); setLucreaza(false); }
            }}>
            {lucreaza ? tr("se creează…") : tr("creează planul")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Șabloane ───────────────────────────────────────────────────────────────

function Sabloane({ poatePlanifica }) {
  const [lista, setLista] = useState([]);
  const [inactive, setInactive] = useState(false);
  const [deschis, setDeschis] = useState(null);
  const [incarca, setIncarca] = useState(true);
  const [eroare, setEroare] = useState("");

  const reincarca = useCallback(async () => {
    setIncarca(true);
    try { setLista(await api.sabloaneMentenanta({ inactive })); }
    catch (e) { setEroare(mesajEroare(e)); }
    finally { setIncarca(false); }
  }, [inactive]);

  useEffect(() => { reincarca(); }, [reincarca]);

  return (
    <>
      {eroare && <div className="eroare">{eroare}</div>}

      <div className="panou panou-atentie">
        <div className="panou-corp">
          <strong>{tr("Șabloanele livrate sunt un punct de plecare, nu procedura firmei.")}</strong>{" "}
          {tr("Sunt derivate din standardele de securitate: EN 62676 pentru video și pentru recunoașterea numerelor, EN 60839-11 pentru control acces, EN 50131 pentru efracție, EN 54 pentru incendiu. Cel de comunicații radio vine din practica de exploatare — acolo nu există un standard care să enumere operațiile. Când ai procedura voastră, se importă ca versiune nouă, iar acestea rămân în istoric.")}
        </div>
      </div>

      <div className="panou">
        <div className="panou-corp">
          <label className="comutator-dezactivate">
            <input type="checkbox" checked={inactive}
              onChange={(e) => setInactive(e.target.checked)} />
            {tr("arată și șabloanele retrase")}
          </label>
        </div>
      </div>

      {incarca ? (
        <div className="gol" style={{ padding: "2rem 0" }}>{tr("Se încarcă…")}</div>
      ) : (
        <div className="panou">
          <table className="tabel">
            <thead>
              <tr>
                <th>{tr("Șablon")}</th><th>{tr("Tip sistem")}</th><th>{tr("Versiune")}</th>
                <th>{tr("Rânduri")}</th><th>{tr("Folosit de")}</th><th />
              </tr>
            </thead>
            <tbody>
              {lista.map((s) => (
                <tr key={s.id} className={s.activ ? "" : "tip-dezactivat"}>
                  <td>
                    {tr(s.denumire)}
                    {s.esteStandard && <span className="marcaj">{tr("livrat")}</span>}
                  </td>
                  <td>{tr(s.tipSistem?.denumire)} <span className="cod-mic">{s.tipSistem?.cod}</span></td>
                  <td>{tr("v")}{s.versiune}</td>
                  <td>{s._count?.linii}</td>
                  <td>
                    {s._count?.planuri} {s._count?.planuri === 1 ? tr("plan") : tr("planuri")}
                    {s._count?.executii > 0 && (
                      <span className="fara-pret">
                        {tr("rulat de")} {s._count.executii} {s._count.executii === 1 ? tr("dată") : tr("ori")}
                      </span>
                    )}
                  </td>
                  <td className="celula-actiuni">
                    <button className="buton buton-mic buton-discret"
                      onClick={async () => {
                        try { setDeschis(await api.sablonMentenanta(s.id)); }
                        catch (e) { setEroare(mesajEroare(e)); }
                      }}>
                      {tr("deschide")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {deschis && (
        <DetaliuSablon
          sablon={deschis} poatePlanifica={poatePlanifica}
          laInchidere={() => setDeschis(null)}
          laSchimbare={async (id) => {
            setDeschis(await api.sablonMentenanta(id ?? deschis.id));
            await reincarca();
          }}
        />
      )}
    </>
  );
}

function DetaliuSablon({ sablon, poatePlanifica, laInchidere, laSchimbare }) {
  const [lucreaza, setLucreaza] = useState(false);
  const [eroare, setEroare] = useState("");

  const areRanduriNoi = (sablon.randuriNoiInStandard?.length ?? 0) > 0;

  return (
    <div className="modal-fundal" onClick={laInchidere}>
      <div className="modal modal-lat modal-checklist" onClick={(e) => e.stopPropagation()}>
        <h3>
          {tr(sablon.denumire)}
          <span className="eticheta" style={{ marginLeft: ".5rem" }}>{tr("v")}{sablon.versiune}</span>
        </h3>
        <p className="subtitlu">
          {tr(sablon.tipSistem?.denumire)} · {sablon.linii?.length} {tr("rânduri")}
          {sablon.descriere ? ` · ${sablon.descriere}` : ""}
        </p>

        {eroare && <div className="eroare">{eroare}</div>}

        {areRanduriNoi && (
          <div className="panou panou-atentie"><div className="panou-corp">
            <strong>
              {tr("Setul livrat de noi a căpătat între timp")}{" "}
              {sablon.randuriNoiInStandard.length}{" "}
              {sablon.randuriNoiInStandard.length === 1 ? tr("rând nou") : tr("rânduri noi")}.
            </strong>{" "}
            {tr("Nu le-am adăugat pe tăcute aici: o listă de verificare care crește singură e o surpriză, nu o îmbunătățire. Butonul „adu rândurile noi\" face o versiune nouă cu ele puse la coadă — rândurile voastre rămân neatinse, iar versiunea de acum rămâne în istoric, legată de ce s-a bifat cu ea.")}
            <ul className="lista-erori">
              {sablon.randuriNoiInStandard.map((l) => (
                <li key={l.proces}>
                  {tr(l.proces)}
                  {l.standard && <span className="cod-mic">{l.standard}</span>}
                </li>
              ))}
            </ul>
          </div></div>
        )}

        {!sablon.poateFiEditat && (
          <div className="panou panou-atentie"><div className="panou-corp">
            <strong>{tr("Șablonul a fost deja rulat pe teren.")}</strong> {tr("Nu se mai editează: o modificare ar rescrie ce s-a bifat cândva. Pentru schimbări, se face o versiune nouă — execuțiile vechi rămân legate de versiunea lor.")}
          </div></div>
        )}

        <div className="panou tabel-derulabil">
          <table className="tabel tabel-operatii">
            <thead>
              <tr>
                <th>#</th><th>{tr("Proces")}</th><th>{tr("Frecvență")}</th>
                <th>{tr("Standard")}</th><th>{tr("Cerințe")}</th>
              </tr>
            </thead>
            <tbody>
              {(sablon.linii ?? []).map((l, i) => (
                <tr key={l.id}>
                  <td>{i + 1}</td>
                  <td>
                    {tr(l.proces)}
                    {l.cheie && <span className="cod-mic">{l.cheie}</span>}
                    {l.specificatie && <span className="fara-pret">{tr(l.specificatie)}</span>}
                    {l.unelteMateriale && (
                      <span className="fara-pret">{tr("unelte:")} {tr(l.unelteMateriale)}</span>
                    )}
                  </td>
                  <td>{l.frecventaEticheta ?? eticheta(FRECVENTE, l.frecventa)}</td>
                  <td className="standard">{l.standard ?? "—"}</td>
                  <td>
                    {!l.obligatorie && <span className="marcaj">{tr("opțional")}</span>}
                    {l.necesitaValoare && <span className="marcaj">{tr("măsurătoare")}</span>}
                    {l.necesitaFoto && <span className="marcaj">{tr("fotografie")}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="modal-actiuni">
          <button className="buton buton-sters" onClick={laInchidere}>{tr("închide")}</button>
          {poatePlanifica && (
            <button
              className={areRanduriNoi ? "buton buton-sters" : "buton"}
              disabled={lucreaza}
              onClick={async () => {
                setLucreaza(true); setEroare("");
                try {
                  const nou = await api.versiuneNouaSablon(sablon.id);
                  await laSchimbare(nou.id);
                } catch (e) { setEroare(mesajEroare(e)); }
                finally { setLucreaza(false); }
              }}>
              {tr("versiune nouă")}
            </button>
          )}
          {poatePlanifica && areRanduriNoi && (
            <button className="buton" disabled={lucreaza}
              onClick={async () => {
                setLucreaza(true); setEroare("");
                try {
                  const nou = await api.versiuneDinStandard(sablon.id);
                  await laSchimbare(nou.id);
                } catch (e) { setEroare(mesajEroare(e)); }
                finally { setLucreaza(false); }
              }}>
              {tr("adu rândurile noi")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Checklist-ul de teren ──────────────────────────────────────────────────

export function ChecklistTeren({ eu, vizitaId, laInchidere }) {
  const [c, setC] = useState(null);
  const [ciorna, setCiorna] = useState({});
  const [lucreaza, setLucreaza] = useState(false);
  const [eroare, setEroare] = useState("");
  const [semnatura, setSemnatura] = useState(null);

  const poateBifa = TEREN.includes(eu.rol);

  // O vizită de mentenanță făcută de mână n-a trecut prin generator, deci
  // n-are checklist. Nu e o eroare — e primul pas: se alege șablonul.
  const [lipsa, setLipsa] = useState(false);

  const reincarca = useCallback(async () => {
    try { setC(await api.checklist(vizitaId)); setLipsa(false); }
    catch (e) {
      if (e?.status === 404) setLipsa(true);
      else setEroare(mesajEroare(e));
    }
  }, [vizitaId]);

  useEffect(() => { reincarca(); }, [reincarca]);

  if (lipsa) {
    return (
      <AlegeSablon
        eu={eu} vizitaId={vizitaId}
        laInchidere={laInchidere}
        laDeschidere={async () => { await reincarca(); }}
      />
    );
  }

  if (!c) {
    return (
      <div className="modal-fundal" onClick={laInchidere}>
        <div className="modal" onClick={(e) => e.stopPropagation()}>
          {eroare ? <div className="eroare">{eroare}</div> : <div className="gol">{tr("Se încarcă…")}</div>}
        </div>
      </div>
    );
  }

  const inchis = c.status === "Finalizat";

  function bifa(linie, camp, valoare) {
    setCiorna((x) => ({
      ...x,
      [linie.id]: { ...(x[linie.id] ?? { rezultat: linie.rezultat?.rezultat }), [camp]: valoare },
    }));
  }
  const valoareCurenta = (linie, camp) =>
    ciorna[linie.id]?.[camp] ?? linie.rezultat?.[camp] ?? "";

  async function salveaza() {
    const rezultate = Object.entries(ciorna)
      .filter(([, v]) => v.rezultat)
      .map(([sablonLinieId, v]) => ({
        sablonLinieId,
        rezultat: v.rezultat,
        ...(v.valoare ? { valoare: v.valoare } : {}),
        ...(v.observatie ? { observatie: v.observatie } : {}),
      }));
    if (!rezultate.length) return;
    setLucreaza(true); setEroare("");
    try {
      setC(await api.completeazaChecklist(vizitaId, { rezultate }));
      setCiorna({});
    } catch (e) { setEroare(mesajEroare(e)); }
    finally { setLucreaza(false); }
  }

  return (
    <div className="modal-fundal" onClick={laInchidere}>
      <div className="modal modal-lat modal-checklist" onClick={(e) => e.stopPropagation()}>
        <h3>
          {tr("Checklist de mentenanță")}
          <span className="eticheta" style={{ marginLeft: ".5rem" }}>
            {inchis ? tr("finalizat") : tr("în lucru")}
          </span>
        </h3>
        <p className="subtitlu">
          {tr(c.vizita?.client?.denumire)} · {tr(c.vizita?.locatie?.denumire)} ·{" "}
          {tr(c.sablon?.denumire)} <span className="cod-mic">{tr("v")}{c.sablonVersiune}</span>
        </p>

        {eroare && <div className="eroare">{eroare}</div>}

        {inchis && (
          <div className="panou panou-atentie"><div className="panou-corp">
            <strong>{tr("Completat de")} {c.completatDeNume}</strong>
            {c.completatLa ? tr(", la {0}", new Date(c.completatLa).toLocaleString("ro-RO")) : ""}.
            {c.numeReprezentantClient ? tr(" Confirmat de {0}.", c.numeReprezentantClient) : ""}
            {" "}{tr("Ce s-a constatat pe teren nu se mai rescrie.")}
          </div></div>
        )}

        <div className="panou tabel-derulabil">
          <table className="tabel tabel-operatii">
            <thead>
              <tr>
                <th>#</th><th>{tr("Proces")}</th><th>{tr("Rezultat")}</th><th>{tr("Valoare / observație")}</th>
              </tr>
            </thead>
            <tbody>
              {c.linii.map((l, i) => {
                const rez = ciorna[l.id]?.rezultat ?? l.rezultat?.rezultat ?? "";
                const neconform = rez === "Neconform" || rez === "RemediatPeLoc";
                return (
                  <tr key={l.id} className={rez === "Neconform" ? "rand-neconform" : ""}>
                    <td>{i + 1}</td>
                    <td>
                      {tr(l.proces)}
                      {l.cheie && <span className="cod-mic">{l.cheie}</span>}
                      {l.specificatie && <span className="fara-pret">{tr(l.specificatie)}</span>}
                      {!l.obligatorie && <span className="marcaj">{tr("opțional")}</span>}
                    </td>
                    <td>
                      <select className="camp" value={rez} disabled={inchis || !poateBifa}
                        onChange={(e) => bifa(l, "rezultat", e.target.value)}>
                        <option value="">—</option>
                        {Object.entries(REZULTATE).map(([v, e]) => (
                          <option key={v} value={v}>{tr(e)}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      {inchis ? (
                        <>
                          {l.rezultat?.valoare && (
                            <span className="valoare-inchisa">
                              <strong>{l.rezultat.valoare}</strong>
                            </span>
                          )}
                          {l.rezultat?.observatie && (
                            <span className="valoare-inchisa">{l.rezultat.observatie}</span>
                          )}
                          {!l.rezultat?.valoare && !l.rezultat?.observatie && (
                            <span className="fara-pret">—</span>
                          )}
                        </>
                      ) : (
                        <>
                          {l.necesitaValoare && (
                            <input className="camp" placeholder={tr("valoarea măsurată")}
                              disabled={!poateBifa}
                              value={valoareCurenta(l, "valoare")}
                              onChange={(e) => bifa(l, "valoare", e.target.value)} />
                          )}
                          {neconform && (
                            <input className="camp"
                              placeholder={rez === "Neconform"
                                ? tr("ce e în neregulă — devine restanță")
                                : tr("ce s-a remediat pe loc")}
                              disabled={!poateBifa}
                              value={valoareCurenta(l, "observatie")}
                              onChange={(e) => bifa(l, "observatie", e.target.value)} />
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {!inchis && (
          <p className="nota-cautare">
            {tr("Fiecare rând marcat")} <strong>{tr("neconform")}</strong> {tr("și neremediat devine automat o restanță în backlogul tehnic — nu se pierde. „Remediat pe loc\" nu produce restanță: chiar asta e diferența dintre cele două.")}
          </p>
        )}

        {!inchis && c.necompletateObligatorii > 0 && (
          <p className="nota-cautare">
            {tr("Mai sunt")} <strong>{c.necompletateObligatorii}</strong> {tr("rânduri obligatorii nebifate. Un checklist pe jumătate nu dovedește nimic.")}
          </p>
        )}

        <div className="modal-actiuni">
          <button className="buton buton-sters" onClick={laInchidere}>{tr("închide")}</button>
          {!inchis && poateBifa && (
            <>
              <button className="buton" disabled={lucreaza || !Object.keys(ciorna).length}
                onClick={salveaza}>
                {lucreaza ? tr("se salvează…") : tr("salvează bifele")}
              </button>
              <button className="buton buton-actiune"
                disabled={lucreaza || !c.sePoateFinaliza || Object.keys(ciorna).length > 0}
                title={Object.keys(ciorna).length > 0 ? tr("Salvează întâi bifele nesalvate.") : undefined}
                onClick={() => setSemnatura({})}>
                {tr("finalizează")}
              </button>
            </>
          )}
        </div>

        {semnatura && (
          <Finalizare
            neconforme={c.neconforme}
            laInchidere={() => setSemnatura(null)}
            laConfirmare={async (dto) => {
              setLucreaza(true); setEroare("");
              try {
                const r = await api.finalizeazaChecklist(vizitaId, dto);
                setSemnatura(null);
                setC(r);
              } catch (e) { setEroare(mesajEroare(e)); }
              finally { setLucreaza(false); }
            }}
          />
        )}
      </div>
    </div>
  );
}

function AlegeSablon({ eu, vizitaId, laInchidere, laDeschidere }) {
  const [sabloane, setSabloane] = useState(null);
  const [ales, setAles] = useState("");
  const [lucreaza, setLucreaza] = useState(false);
  const [eroare, setEroare] = useState("");

  const poateDeschide = TEREN.includes(eu.rol);

  useEffect(() => {
    api.sabloanePentruVizita(vizitaId)
      .then((l) => {
        setSabloane(l);
        const primul = l.find((s) => s.potrivit);
        if (primul) setAles(primul.id);
      })
      .catch((e) => setEroare(mesajEroare(e)));
  }, [vizitaId]);

  return (
    <div className="modal-fundal" onClick={laInchidere}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>{tr("Deschide checklistul")}</h3>
        <p className="subtitlu">
          {tr("Vizita n-are încă un checklist — a fost programată de mână, nu dintr-un plan de mentenanță. Alege șablonul după care se verifică.")}
        </p>

        {eroare && <div className="eroare">{eroare}</div>}

        {sabloane === null ? (
          <div className="gol">{tr("Se încarcă…")}</div>
        ) : sabloane.length === 0 ? (
          <div className="panou panou-atentie"><div className="panou-corp">
            <strong>{tr("Niciun șablon de mentenanță.")}</strong> {tr("Se adaugă din secțiunea „Mentenanță → Șabloane\".")}
          </div></div>
        ) : (
          <>
            <label className="camp">{tr("Șablon")}
              <select value={ales} onChange={(e) => setAles(e.target.value)}>
                <option value="">{tr("— alege —")}</option>
                {sabloane.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.potrivit ? "" : "⚠ "}{tr(s.denumire)} {tr("(v")}{s.versiune}, {s._count?.linii} {tr("rânduri)")}
                  </option>
                ))}
              </select>
            </label>
            {/* Nimic nu dispare tăcut: cele nepotrivite rămân alegibile, cu motivul scris. */}
            {ales && !sabloane.find((s) => s.id === ales)?.potrivit && (
              <p className="nota-cautare">
                {sabloane.find((s) => s.id === ales)?.motiv} {tr("Îl poți folosi oricum, dacă știi de ce.")}
              </p>
            )}
          </>
        )}

        <div className="modal-actiuni">
          <button className="buton buton-sters" onClick={laInchidere}>{tr("închide")}</button>
          {poateDeschide && (
            <button className="buton buton-actiune"
              disabled={lucreaza || !ales}
              onClick={async () => {
                setLucreaza(true); setEroare("");
                try {
                  await api.deschideChecklist(vizitaId, ales);
                  await laDeschidere();
                } catch (e) { setEroare(mesajEroare(e)); setLucreaza(false); }
              }}>
              {lucreaza ? tr("se deschide…") : tr("deschide checklistul")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Finalizare({ neconforme, laInchidere, laConfirmare }) {
  const [completatDeNume, setNume] = useState("");
  const [numeReprezentantClient, setClient] = useState("");
  const [lucreaza, setLucreaza] = useState(false);

  return (
    <div className="modal-fundal" onClick={laInchidere}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>{tr("Finalizează checklistul")}</h3>
        {neconforme > 0 && (
          <div className="panou panou-atentie"><div className="panou-corp">
            <strong>{neconforme} {neconforme === 1 ? tr("rând neconform") : tr("rânduri neconforme")}</strong>{" "}
            {neconforme === 1 ? tr("va deveni restanță") : tr("vor deveni restanțe")} {tr("în backlogul tehnic. Nu se pierd.")}
          </div></div>
        )}
        <label className="camp">{tr("Completat de")}
          <input value={completatDeNume} onChange={(e) => setNume(e.target.value)}
            placeholder={tr("Numele tehnicianului")} />
        </label>
        <label className="camp">{tr("Reprezentantul clientului (opțional)")}
          <input value={numeReprezentantClient} onChange={(e) => setClient(e.target.value)}
            placeholder={tr("Cine a confirmat pe loc")} />
        </label>
        <p className="nota-cautare">
          {tr("După finalizare, scadența sistemului și a echipamentelor lui se mută înainte, iar checklistul nu se mai poate schimba.")}
        </p>
        <div className="modal-actiuni">
          <button className="buton buton-sters" onClick={laInchidere}>{tr("renunță")}</button>
          <button className="buton buton-actiune"
            disabled={lucreaza || completatDeNume.trim().length < 3}
            onClick={async () => {
              setLucreaza(true);
              await laConfirmare({
                completatDeNume: completatDeNume.trim(),
                ...(numeReprezentantClient.trim()
                  ? { numeReprezentantClient: numeReprezentantClient.trim() } : {}),
              });
              setLucreaza(false);
            }}>
            {lucreaza ? tr("se finalizează…") : tr("finalizează")}
          </button>
        </div>
      </div>
    </div>
  );
}
